import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import api, { apiErr, pinSessionToken, clearSessionPin, pinnedSessionToken } from "@/lib/api";
import { refreshAssetToken, clearAssetToken } from "@/lib/assetToken";

const AuthContext = createContext(null);

// ─── WHICH SIGN-IN IS THE CURRENT ONE (ZEN-432 + ZEN-433) ────────────────────
//
// These two tickets are one carelessness seen from two sides: nothing in this
// file could say WHICH sign-in was the current one, so three separate things
// went on behaving as though the sign-in they started with was still the one.
//
//   1. THE WHO-AM-I CHECK RAN ON REGARDLESS. On start-up this file asks the
//      server "who am I?". If somebody signs in while that question is still in
//      the air, the answer still lands — and it either puts the PREVIOUS
//      person's name and role back on screen while every request runs as the new
//      account, or (on its failure path) deletes the stored pass, whichever pass
//      that now is. So a successful sign-in could end in being signed out.
//
//   2. THE CHOSEN BENEFICIARY OUTLIVED THE ACCOUNT. Signing in already threw
//      away the previous person's file credential, but not the beneficiary they
//      had selected — that id sat in browser storage and rode along into the
//      next account's requests.
//
//   3. A MOMENT'S BAD CONNECTION LOOKED LIKE A DEAD SESSION. Any failure at all
//      of the who-am-I check deleted the stored pass. A dropped signal for one
//      second signed the person out of an app holding their tax records.
//
//   4. And a second TAB was never told any of this had happened (ZEN-433). The
//      pass lives in storage every tab shares; each tab's idea of who is signed
//      in does not. An older tab kept showing the first family while its
//      requests went out as the second.
//
// ONE ANSWER FOR ALL FOUR: a generation number. Every sign-in, sign-out, and
// change made by another tab bumps it. Anything asynchronous captures the number
// it started under and does nothing at all if it comes back to a world that has
// moved on. That is not a new idea here — lib/assetToken.js has used exactly
// this shape since the file-credential work, for exactly this reason — so this
// is the same pattern applied to the thing that decides identity, rather than a
// second mechanism to keep in step with the first.
//
// WHAT IS DELIBERATELY NOT DONE. Retiring a sign-in on OTHER devices needs the
// server to remember sessions, and it does not: a pass is a self-contained,
// seven-day token with no record kept of it. Changing a password already ends
// every session everywhere (each pass carries a fingerprint of the password it
// was issued under). "Signing in on my laptop should sign my old phone out" is a
// real, separate, unbuilt feature — see the note in the pull request.

// HOW LONG TO WAIT BEFORE TRYING THE WHO-AM-I CHECK AGAIN after a connection
// failure. The failure this exists for is a blip — a tunnel, a handover between
// wifi and mobile — which is over in well under a second. One quiet retry means
// the ordinary case never shows anybody a screen at all; if the second attempt
// also fails, the connection is genuinely down and saying so is the honest move.
const RECHECK_AFTER_BLIP_MS = 800;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=anon, obj=auth
  const [selectedStudentId, setSelectedStudentId] = useState(localStorage.getItem("sid") || null);

  // WHO IS ON SCREEN, readable from an event handler without going through a
  // closure. The storage listener below used to read `user` from its closure and
  // re-register whenever it changed — which leaves a gap: React commits the new
  // identity, paints, and only THEN swaps the listener. A storage event landing
  // in that gap runs the OLD listener, sees nobody on screen, and quietly adopts
  // the replacement instead of stopping the tab — with the previous account
  // still rendered and able to submit. Found by adversarial review, round 2.
  //
  // Assigned during render on purpose. An effect would reintroduce the very gap
  // this closes. A render that is later thrown away can only make this value too
  // NEW, never too old, and too new errs toward stopping the tab, which is the
  // safe direction.
  const userRef = useRef(user);
  userRef.current = user;

  // Bumped by every event that changes who this tab is. Anything async compares
  // the number it began under against this before it is allowed to write.
  const generation = useRef(0);

  // Set when ANOTHER tab changed the sign-in out from under this one: "replaced"
  // (somebody signed in) or "ended" (somebody signed out). Non-null means this
  // tab shows a notice instead of the app — see the reasoning at the render.
  const [changedByAnotherTab, setChangedByAnotherTab] = useState(null);

  // Set when the who-am-I check could not be answered for a reason that is NOT
  // "this sign-in is no longer valid". The stored pass is kept.
  const [cannotReach, setCannotReach] = useState(false);

  const forgetSelectedBeneficiary = useCallback(() => {
    // Unconditional, exactly like clearAssetToken() below it at each call site.
    // Deciding by comparing accounts would need to know who was here BEFORE,
    // which at sign-in time this file often does not (an expired session leaves
    // no name behind). The cost of always forgetting is that somebody signing in
    // again re-picks a beneficiary; the cost of sometimes remembering is one
    // family's beneficiary id travelling into another family's requests.
    localStorage.removeItem("sid");
    setSelectedStudentId(null);
  }, []);

  // ── THE ONE QUESTION EVERY ASYNCHRONOUS PATH HERE MUST ASK ─────────────────
  //
  // Three rounds of adversarial review found six separate defects and ALL SIX
  // were the same shape: something read before an await, or reported after one,
  // that was no longer true by the time it was used. Patching the sites one at a
  // time produced a new variant each round, which is the signal to stop patching
  // sites and write the rule once.
  //
  // IT HAS TWO HALVES AND NEITHER IS SUFFICIENT ALONE:
  //
  //   * the generation number catches what THIS tab did — a sign-in, a sign-out,
  //     a cross-tab change it has already processed. It is the only half that can
  //     see a change which left the same pass in place.
  //
  //   * the pass comparison catches what ANOTHER tab did, and it is the stronger
  //     half, because shared storage is the truth NOW and does not wait for an
  //     event to be delivered. Round 3 found a retired request deleting the new
  //     sign-in precisely because it consulted only the generation, and the
  //     storage event announcing the change had not arrived yet.
  //
  // So: an operation is still current only if the world has not moved on in
  // EITHER sense, and every await in this file is followed by this question.
  const stillCurrent = useCallback(
    (gen, token) =>
      gen === generation.current && localStorage.getItem("token") === token,
    []
  );

  // WHAT WAS CURRENT BEFORE A CALLER'S OWN REQUEST STARTED.
  //
  // Most paths here own both halves — they make the request and install the
  // result, so they can capture before and check after. The password-reset lane
  // cannot: the sign-in page sends the request and only then calls
  // adoptSession(), so by the time this file is involved the window has already
  // been open for a whole round trip.
  //
  // Round 5 tried to close that from inside adoptSession by comparing the pin
  // against storage. Round 6 broke it: if the other tab's storage event ARRIVES
  // during the request, this tab pins the newer session itself, the two agree,
  // and the stale reset installs over it anyway. Nothing observable after the
  // fact can tell the difference — the snapshot has to be taken BEFORE.
  const sessionSnapshot = useCallback(
    () => ({ gen: generation.current, token: localStorage.getItem("token") }),
    []
  );

  const loadMe = useCallback(async (attempt = 0) => {
    const mine = generation.current;
    const token = localStorage.getItem("token");
    if (!token) {
      clearSessionPin();
      setCannotReach(false);
      setUser(false);
      return;
    }
    // Pinned BEFORE the request, not after the answer: from this moment on, this
    // tab is acting as this exact pass, and lib/api.js refuses to send anything
    // under a different one. Pinning after the answer would leave the whole
    // start-up window unguarded, which is the window ZEN-432 is about.
    pinSessionToken(token);
    try {
      const { data } = await api.get("/auth/me");
      if (!stillCurrent(mine, token)) return;    // a newer sign-in got here first
      // Awaited before setUser: the moment user is set, components render and
      // their file URLs are built. Without a token in hand every image 401s on
      // first paint.
      await refreshAssetToken();
      if (!stillCurrent(mine, token)) return;    // ...and again, across the await
      setCannotReach(false);
      setUser(data);
    } catch (e) {
      // THE PASS COMPARISON EARNS ITS KEEP HERE. A refusal belonging to a
      // sign-in that another tab has already replaced must not delete the
      // replacement — and the generation alone cannot see that, because the
      // storage event carrying the news may not have arrived. (Round 3.)
      if (!stillCurrent(mine, token)) return;
      // ONLY A DEFINITIVE REFUSAL MAY THROW THE PASS AWAY. A 401 is the server
      // saying "this sign-in is over". No response at all, a timeout, or a 500
      // is the server saying nothing — and a silence is not a revocation. The
      // old code could not tell the two apart and deleted the pass for both.
      if (e?.response?.status === 401) {
        localStorage.removeItem("token");
        clearSessionPin();
        setCannotReach(false);
        setUser(false);
        return;
      }
      if (attempt === 0) {
        setTimeout(() => {
          if (!stillCurrent(mine, token)) return;
          loadMe(1);
        }, RECHECK_AFTER_BLIP_MS);
        return;
      }
      // Still signed in as far as anybody knows; we simply cannot ask.
      setCannotReach(true);
    }
  }, [stillCurrent]);

  useEffect(() => { loadMe(); }, [loadMe]);

  // ── ANOTHER TAB CHANGED THE SIGN-IN (ZEN-433) ──────────────────────────────
  //
  // The browser fires this event in every OTHER tab of the site when shared
  // storage changes — never in the tab that made the change. So this only ever
  // runs in a tab that is now out of date.
  //
  // A tab showing somebody's records STOPS rather than quietly following the new
  // sign-in. Following would mean the screen changes account under a person who
  // did not ask it to, mid-sentence, with a half-filled form still on it — and
  // that form is precisely how the first family's figures end up written into
  // the second family's records. Stopping loses whatever was typed here, which
  // is a real cost, and it is the smaller one: the alternative loses it too, by
  // filing it in the wrong place.
  //
  // A tab showing nobody (sitting at the sign-in screen) has nothing to protect,
  // so it just picks up the new state without saying anything.
  useEffect(() => {
    const onStorage = (e) => {
      // Only the sign-in pass. A beneficiary change (key "sid") in another tab
      // is left ALONE on purpose: two tabs open on two children is a normal
      // thing to do, both are inside the same account, and the server authorises
      // every request against that account regardless.
      // e.key === null means the whole store was cleared, which counts.
      if (e.key !== null && e.key !== "token") return;
      const stored = localStorage.getItem("token");
      if (stored === pinnedSessionToken()) return;   // nothing changed for us
      generation.current += 1;                       // retire anything in flight
      clearAssetToken();
      if (!userRef.current) {                        // nobody on screen to protect
        // THE CHOSEN BENEFICIARY GOES TOO. This tab is about to start acting as
        // a DIFFERENT account, and the selected child is exactly what must not
        // cross that line — the same rule login() and adoptSession() already
        // follow. Found by adversarial review (2026-09-04): the first version of
        // this branch adopted the new sign-in and kept the previous family's
        // child id, which is defect (2) of ZEN-432 reintroduced by the very
        // change that fixes it everywhere else.
        forgetSelectedBeneficiary();
        setChangedByAnotherTab(null);
        loadMe();
        return;
      }
      setCannotReach(false);
      setChangedByAnotherTab(stored ? "replaced" : "ended");
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
    // Registered ONCE, deliberately. Identity is read live through the ref
    // above, so this listener never has to be torn down and rebuilt as the
    // signed-in person changes — and there is therefore no window in which the
    // wrong one is installed. Both dependencies are stable useCallbacks with no
    // dependencies of their own, so naming them costs nothing and keeps this
    // effect honest about what it uses.
  }, [loadMe, forgetSelectedBeneficiary]);

  const login = async (email, password) => {
    // Invalidate any credential or in-flight mint belonging to whoever was
    // here before. Logging in without logging out first is a real path (an
    // expired session bounces to /auth), and without this the new user can
    // inherit the previous user's asset token.
    clearAssetToken();
    // Captured BEFORE the request. Bumping only afterwards let a SLOWER sign-in
    // land last, bump the number itself, and install over the newer session that
    // had already arrived — its own guard could never fire, because it wrote the
    // number it was about to compare against. (Round 3.)
    const startedAt = generation.current;
    const startingToken = localStorage.getItem("token");
    const { data } = await api.post("/auth/login", { email, password });
    // Somebody is signed in and it is not this request. Leave them alone.
    // BOTH halves of the rule, not just the generation: another tab can replace
    // the pass while this request is in the air, and the event announcing it
    // arrives later than the answer does. (Round 4 — this guard was added in
    // round 3 and left using the weaker half, which is exactly the mistake the
    // shared rule exists to make impossible.)
    if (!stillCurrent(startedAt, startingToken)) return data.user;
    // Bumped the instant the new pass exists and before it is stored, so no
    // older who-am-I answer can land afterwards and speak for this tab. Bumping
    // BEFORE the request instead would retire the previous session even when the
    // password turns out to be wrong.
    generation.current += 1;
    const mine = generation.current;
    localStorage.setItem("token", data.token);
    pinSessionToken(data.token);
    forgetSelectedBeneficiary();
    setCannotReach(false);
    setChangedByAnotherTab(null);
    await refreshAssetToken();
    // THIS SIGN-IN CAN ITSELF BE OVERTAKEN, across that await, by another tab.
    // Bumping the number is not enough — this continuation has to check it too,
    // or it paints THIS person's name over a tab that is already pinned to
    // somebody else's session, which is the exact mismatch the whole change
    // exists to prevent. Found by adversarial review (2026-09-04).
    //
    // The sign-in itself genuinely succeeded, so the caller is still told who it
    // signed in as. What is withheld is the IDENTITY ON SCREEN: the newer
    // session's own who-am-I answer paints that, and it is the one telling the
    // truth about what this tab now is.
    if (!stillCurrent(mine, data.token)) return data.user;
    setUser(data.user);
    return data.user;
  };

  // Completing a password reset signs you IN, rather than sending you back to
  // the sign-in screen (ZEN-145).
  //
  // Anyone who knows an address can trip its sign-in attempt limit on purpose,
  // and can re-trip it the moment the window turns over — including in the
  // instant after a reset. So a recovery lane that ends at the sign-in screen
  // is a lane the owner has to win a race to use, which is no lane at all.
  // The server returns a session with the reset; this installs it, exactly as
  // signing in would.
  //
  // A server that returns no session — a revoked beneficiary, or a database
  // hiccup while checking — is NOT an error: the password really did change.
  // Returning false lets the screen fall back to "now sign in".
  const adoptSession = async (data, snapshot) => {
    if (!data?.token || !data?.user) return false;
    // THE CALLER'S OWN REQUEST HAPPENED BEFORE WE WERE CALLED, and the world can
    // move during it — a password reset is answered while another tab signs in.
    // The caller cannot be asked to guard that: the reset lives on the sign-in
    // page, which is not this lane's file, and a rule that every future caller
    // must remember is a rule that will eventually be forgotten. So the guard
    // goes HERE, where every caller gets it for free.
    //
    // The pin is what this tab believes is current, and it tracks storage
    // exactly — set when a session is installed, cleared when there is none. A
    // divergence therefore means another tab wrote a pass while the caller was
    // waiting, and this session must not be installed over it. (Round 5.)
    // A caller that made its own request hands back the pass that was current
    // BEFORE it started; one that did not is judged on what this tab believes
    // now. The snapshot is what round 6 needed: once the other tab's news has
    // been delivered, this tab has already pinned the newer session, so anything
    // measured AFTER the fact agrees with itself and sees nothing wrong.
    //
    // BOTH halves, and the second one was removed once and had to come back.
    // The argument for dropping it was that no case could be built where the
    // generation catches something the pass does not. Round 7 built it: sign IN
    // and then OUT in another tab while the reset is in flight, and the pass ends
    // exactly where it started — null — so the pass comparison sees nothing,
    // while the person has deliberately signed out everywhere and this stale
    // reset would sign them back in. A pass is a value; the generation is a
    // count of events. Only a count can see a round trip that returns home.
    const knownToken = snapshot ? snapshot.token : pinnedSessionToken();
    const knownGen = snapshot ? snapshot.gen : generation.current;
    const storedNow = localStorage.getItem("token");
    if (storedNow !== knownToken || knownGen !== generation.current) {
      return !!storedNow;
    }
    clearAssetToken();
    generation.current += 1;
    const mine = generation.current;
    localStorage.setItem("token", data.token);
    pinSessionToken(data.token);
    forgetSelectedBeneficiary();
    setCannotReach(false);
    setChangedByAnotherTab(null);
    await refreshAssetToken();
    // Same overtaking risk as login(), and the answer has to be more careful
    // here, because this boolean is a CLAIM: the sign-in page reads it as "a
    // session is installed, say nothing more". If what overtook us was a SIGN-OUT
    // in another tab, there is no session, and answering true leaves somebody
    // staring at an unchanged reset form that looks like it never ran — while
    // their one-time link has in fact been spent. So answer with what is actually
    // in storage: a replacement sign-in means yes, a sign-out means no, and no
    // sends them to "your password changed, now sign in", which is the truth.
    // (Adversarial review, round 2.)
    if (!stillCurrent(mine, data.token)) return !!localStorage.getItem("token");
    setUser(data.user);
    return true;
  };

  // Signing up no longer signs you in (ZEN-226). The server answers the SAME
  // sentence whether the address was free or already had an account, returns no
  // session either way, and emails the difference to the mailbox instead — so a
  // stranger typing addresses into the form learns nothing, while the person who
  // actually owns the address gets either a link to finish, or "you already have
  // an account, here is the way in".
  //
  // clearAssetToken() STAYS. Signing up over the top of an expired session is a
  // real path (an expired session bounces to /auth), and a half-finished sign-up
  // must not leave the previous person's file credential sitting in memory.
  const register = async (email) => {
    clearAssetToken();
    const { data } = await api.post("/auth/register", { email });
    return data.message;
  };

  // Redeeming the emailed link is what creates the account, so THIS is where a
  // session appears. adoptSession already does exactly the right thing — it is
  // the same install the password-reset lane uses.
  // THE NAME AND THE PASSWORD ARE BOTH CHOSEN HERE, and that is a security
  // property rather than a layout preference. Anybody can type a stranger's
  // address into a form that needs no credential. If that form also chose the
  // password, a stranger could seed an address and sign in as whoever clicked;
  // if it chose the NAME, a stranger decided what a family's year-end tax
  // documents are headed. Nothing a stranger types survives to the account —
  // everything on it is chosen by whoever proves they hold the mailbox.
  const confirmSignup = async (token, name, password) => {
    // Same capture-before-the-await as login(), for the same reason: a slower
    // confirmation must not install itself over a session that arrived while it
    // was in the air. The account was still created, so the caller is told so.
    const before = sessionSnapshot();
    const { data } = await api.post("/auth/confirm", { token, name, password });
    if (!stillCurrent(before.gen, before.token)) return !!localStorage.getItem("token");
    return adoptSession(data, before);
  };

  const logout = () => {
    clearAssetToken();
    generation.current += 1;
    localStorage.removeItem("token");
    localStorage.removeItem("sid");
    clearSessionPin();
    setSelectedStudentId(null);
    setCannotReach(false);
    setChangedByAnotherTab(null);
    setUser(false);
    window.location.href = "/auth";
  };

  const selectStudent = (id) => {
    setSelectedStudentId(id);
    if (id) localStorage.setItem("sid", id); else localStorage.removeItem("sid");
  };

  // A tab that has stopped renders the notice INSTEAD OF the app. Rendering it
  // over the top would leave the previous account's figures legible behind it
  // and its forms reachable by keyboard, which is most of what stopping is for.
  if (changedByAnotherTab) {
    return <SessionChangedNotice kind={changedByAnotherTab} />;
  }
  if (cannotReach) {
    return (
      <CannotReachNotice
        onRetry={() => {
          setCannotReach(false);
          loadMe();
        }}
      />
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, register, confirmSignup, logout, adoptSession, sessionSnapshot, selectedStudentId, selectStudent, apiErr }}>
      {children}
    </AuthContext.Provider>
  );
}

// ─── The two notices ─────────────────────────────────────────────────────────
//
// ALL THREE MESSAGES BELOW ARE DRAFT COPY AWAITING APPROVAL (rule 13). They are
// wired up so the behaviour can be reviewed and tested; the exact sentences are
// the owner's call and are quoted in the pull request for that ruling.
//
// THE BUTTON SAYS WHAT HAPPENS, not what the code does. It reloads the page, so
// the tab comes back showing whoever is signed in NOW, on the same screen they
// were already on. The first draft said "Catch this tab up", which was reviewed
// (2026-09-02) as not telling anybody what they would get.
//
// Laid out as a single centred card with a maximum width, so it reads the same
// on a phone as on a laptop with no separate mobile treatment to keep in step.

function NoticeFrame({ heading, body, actionLabel, onAction, testid }) {
  return (
    <div
      data-testid={testid}
      className="flex min-h-screen items-center justify-center bg-[#E9E9EB] p-6"
    >
      <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-sm">
        <h1 className="text-xl font-semibold text-[#1B2733]">{heading}</h1>
        <p className="mt-3 text-sm leading-relaxed text-[#4A5561]">{body}</p>
        <button
          type="button"
          data-testid={`${testid}-action`}
          onClick={onAction}
          className="mt-6 w-full rounded-lg bg-[#034AA2] px-4 py-2.5 text-sm font-medium text-white"
        >
          {actionLabel}
        </button>
      </div>
    </div>
  );
}

export function SessionChangedNotice({ kind }) {
  if (kind === "ended") {
    return (
      <NoticeFrame
        testid="session-ended-notice"
        heading="You were signed out in another tab"
        body="This tab has stopped so it does not keep your records on screen. Anything you had typed here and not yet saved is gone."
        actionLabel="Go to sign in"
        onAction={() => { window.location.href = "/auth"; }}
      />
    );
  }
  return (
    <NoticeFrame
      testid="session-replaced-notice"
      heading="You signed in again somewhere else"
      body="This tab is still showing the account you were signed in as before, so it has stopped here to keep the two from getting mixed up. Anything you had typed on this tab and not yet saved is gone."
      actionLabel="Show the account I'm signed in as now"
      onAction={() => { window.location.reload(); }}
    />
  );
}

export function CannotReachNotice({ onRetry }) {
  return (
    <NoticeFrame
      testid="cannot-reach-notice"
      heading="We can't reach your records right now"
      body="You are still signed in. This looks like a connection problem rather than a sign-in problem. Check your internet connection and try again."
      actionLabel="Try again"
      onAction={onRetry}
    />
  );
}

export const useAuth = () => useContext(AuthContext);
